import React from "react";
import { Play, Clock, Star, ArrowRight, ShoppingCart, BookOpen } from "lucide-react";
import { motion } from "framer-motion";

export default function CourseCard({ course, isPurchased, onAction, onAddToCart }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-600/10 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-900"
      onClick={onAction}
    >
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={course.thumbnail || `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60`}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute left-4 top-4 max-w-[calc(100%-2rem)] truncate rounded-md bg-white/90 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-600 shadow-lg backdrop-blur dark:bg-slate-900/90 dark:text-indigo-400">
          {course.category || "General"}
        </div>
        <div className="absolute inset-0 bg-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="rounded-full border border-white/30 bg-white/20 p-4 backdrop-blur-xl transition-transform group-hover:scale-100">
            <Play className="text-white fill-white" size={28} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1 text-amber-500">
            <Star size={14} fill="currentColor" />
            <span className="text-sm font-black text-slate-900 dark:text-white">4.9</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest">
            <Clock size={12} /> 12h 30m
          </div>
        </div>

        <h3 className="mb-3 min-h-[3.25rem] text-lg font-black leading-snug text-slate-900 transition-colors line-clamp-2 group-hover:text-indigo-600 dark:text-white sm:text-xl">
          {course.title}
        </h3>

        <p className="mb-6 text-sm font-medium leading-relaxed text-slate-500 line-clamp-2 dark:text-slate-400">
          {course.description || "Master the fundamentals and advanced concepts in this comprehensive course."}
        </p>

        <div className="mt-auto flex items-center justify-between gap-4 border-t border-slate-100 pt-5 dark:border-slate-800">
          <div className="flex flex-col">
            {course.original_price > course.price && (
              <span className="text-xs text-slate-400 line-through font-bold">₹{course.original_price}</span>
            )}
            <span className="text-xl font-black text-slate-900 dark:text-white sm:text-2xl">
              {course.price > 0 ? `₹${course.price}` : <span className="text-emerald-500">Free</span>}
            </span>
          </div>
          
          <div className="flex gap-2">
            {!isPurchased && onAddToCart && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddToCart(); }}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400 transition-all hover:bg-indigo-50 hover:text-indigo-600 dark:border-slate-800 dark:bg-slate-800 dark:hover:bg-indigo-900/30"
              >
                <ShoppingCart size={20} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onAction(); }}
              className={`flex h-11 w-11 items-center justify-center rounded-lg shadow-lg transition-all ${isPurchased
                ? "bg-emerald-500 text-white shadow-emerald-200 dark:shadow-none"
                : "bg-indigo-600 text-white shadow-indigo-100 dark:shadow-none hover:bg-indigo-700"
                }`}
            >
              {isPurchased ? <BookOpen size={20} /> : <ArrowRight size={20} />}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
